/**
 * All Dependency-Track findings of one ProduktInstanz.
 *
 * This is the view that replaces the DT UI. It is read-only on purpose: the
 * applicability verdict lives on the Triage page (VulnerabilityEvent cards), so
 * there is exactly one triage truth. DT's own analysis state is shown for
 * transparency but never written from here.
 *
 * Ordering is the server's (KEV first, then severity, then CVSS) and is what the
 * table starts with; the header sorts are an override, not a default.
 */

import CvssScore from '../../components/CvssScore';
import { useMemo, useState, type ReactNode } from 'react';

import CveLink from '../../components/CveLink';
import Banner from '../../components/Banner';
import Chip, { ChipList } from '../../components/Chip';
import DataTable, { type DataTableColumn } from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import { DASH, fmtDate, fmtNumber, fmtVersion, plural } from '../../lib/format';
import type { DtFinding, DtFindingsResponse, DtSeverity } from '../../lib/types';
import { DT_SEVERITY_ORDER } from '../../lib/types';

import { SEVERITY_LABELS, SEVERITY_TONE, fmtEpss, severityRank } from './severity';
import './findings-panel.css';

export interface FindingsPanelProps {
  data: DtFindingsResponse;
  includeSuppressed: boolean;
  onToggleSuppressed: (value: boolean) => void;
}

export function FindingsPanel({
  data,
  includeSuppressed,
  onToggleSuppressed,
}: FindingsPanelProps): ReactNode {
  const [severity, setSeverity] = useState<string>('');
  const [onlyKev, setOnlyKev] = useState(false);
  const [search, setSearch] = useState('');
  const hasFilters = severity !== '' || onlyKev || search !== '' || includeSuppressed;

  const resetFilters = (): void => {
    setSeverity('');
    setOnlyKev(false);
    setSearch('');
    if (includeSuppressed) onToggleSuppressed(false);
  };

  const rows = useMemo((): DtFinding[] => {
    const term = search.trim().toLowerCase();
    return data.findings.filter((finding) => {
      if (severity !== '' && finding.severity !== severity) return false;
      if (onlyKev && !finding.kev) return false;
      if (term === '') return true;
      return (
        finding.cve_id.toLowerCase().includes(term) ||
        finding.component.toLowerCase().includes(term) ||
        fmtVersion(finding.component_version).toLowerCase().includes(term) ||
        (finding.source_label ?? finding.source ?? '').toLowerCase().includes(term) ||
        finding.title.toLowerCase().includes(term)
      );
    });
  }, [data.findings, onlyKev, search, severity]);

  const columns = useMemo(
    (): readonly DataTableColumn<DtFinding>[] => [
      {
        key: 'cve',
        header: 'CVE',
        width: '190px',
        sortValue: (row) => row.cve_id,
        render: (row) => (
          <ChipList>
            <CveLink cve={row.cve_id} tone={row.kev ? 'alert' : 'neutral'} source={row.source} links={row.source_links} />
            {row.kev ? <Chip tone="alert">KEV</Chip> : null}
            {row.suppressed ? <Chip tone="neutral">unterdrückt</Chip> : null}
          </ChipList>
        ),
      },
      {
        key: 'severity',
        header: 'Schwere',
        width: '130px',
        sortValue: (row) => severityRank(row.severity),
        render: (row) => (
          <Chip tone={SEVERITY_TONE[row.severity] ?? 'neutral'}>
            {SEVERITY_LABELS[row.severity] ?? row.severity}
          </Chip>
        ),
      },
      {
        key: 'cvss',
        header: 'CVSS',
        width: '80px',
        align: 'right',
        sortValue: (row) => row.cvss_score ?? -1,
        render: (row) => <CvssScore score={row.cvss_score} />,
      },
      {
        key: 'epss',
        header: 'EPSS',
        width: '90px',
        align: 'right',
        headerTitle: 'Wahrscheinlichkeit einer Ausnutzung in den nächsten 30 Tagen',
        sortValue: (row) => row.epss_score ?? -1,
        render: (row) => fmtEpss(row.epss_score) ?? <span className="muted">{DASH}</span>,
      },
      {
        key: 'component',
        header: 'Komponente',
        cellClassName: 'findings-component',
        sortValue: (row) => `${row.component} ${fmtVersion(row.component_version)}`.toLowerCase(),
        render: (row) => (
          <>
            <div>{row.component || DASH}</div>
            <div className="muted mono">{fmtVersion(row.component_version)}</div>
          </>
        ),
      },
      {
        key: 'published',
        header: 'Veröffentlicht',
        width: '120px',
        sortValue: (row) => String(row.published ?? ''),
        render: (row) => fmtDate(row.published) || <span className="muted">{DASH}</span>,
      },
    ],
    [],
  );

  const renderDetail = (row: DtFinding): ReactNode => (
    <div className="findings-detail">
      <div className="findings-detail-summary">
        <h3>{row.title || row.cve_id}</h3>
        {row.description ? <p>{row.description}</p> : null}
      </div>
      <dl className="findings-metadata">
        {row.cpe ? (
          <div className="findings-identifier">
            <dt>CPE</dt>
            <dd><code className="mono">{row.cpe}</code></dd>
          </div>
        ) : null}
        {row.source_label || row.source ? (
          <div><dt>Quelle</dt><dd>{row.source_label || row.source}</dd></div>
        ) : null}
        {row.epss_percentile != null ? (
          <div><dt>EPSS-Perzentil</dt><dd>{(row.epss_percentile * 100).toFixed(0)} %</dd></div>
        ) : null}
        {row.dt_analysis_state ? (
          <div><dt>Dependency-Track-Status</dt><dd>{row.dt_analysis_state}</dd></div>
        ) : null}
      </dl>
      <p className="findings-detail-note">
        Befunde aus Dependency-Track. Die Anwendbarkeit bewerten Sie unter „Triage“.
      </p>
    </div>
  );

  if (data.scan_state !== 'ok' && data.findings.length === 0) {
    return (
      <div className="card-body">
        <Banner kind={data.scan_state === 'unbekannt' ? 'error' : 'warn'}>
          {data.note ?? 'Für diese Instanz liegen keine Dependency-Track-Daten vor.'}
        </Banner>
      </div>
    );
  }

  return (
    <div className="findings-panel">
      <div className="table-toolbar">
        <input
          type="search"
          value={search}
          placeholder="CVE, Komponente oder Quelle suchen…"
          aria-label="Befunde durchsuchen"
          onChange={(event) => setSearch(event.target.value)}
        />
        <select
          value={severity}
          aria-label="Nach Schwere filtern"
          onChange={(event) => setSeverity(event.target.value)}
        >
          <option value="">Alle Schweregrade</option>
          {DT_SEVERITY_ORDER.map((value: DtSeverity) => (
            <option key={value} value={value}>
              {SEVERITY_LABELS[value]}
            </option>
          ))}
        </select>
        <div className="table-toolbar-options">
          <label
            className="check"
            title="Nur Schwachstellen mit bekannter aktiver Ausnutzung anzeigen (KEV)"
          >
            <input
              type="checkbox"
              checked={onlyKev}
              onChange={(event) => setOnlyKev(event.target.checked)}
            />
            Nur aktiv ausgenutzt
          </label>
          <label
            className="check"
            title="In Dependency-Track unterdrückte Befunde zusätzlich anzeigen. Der Status in Dependency-Track wird dabei nicht geändert."
          >
            <input
              type="checkbox"
              checked={includeSuppressed}
              onChange={(event) => onToggleSuppressed(event.target.checked)}
            />
            Unterdrückte einbeziehen
          </label>
          {hasFilters ? (
            <button type="button" className="link" onClick={resetFilters}>
              Filter zurücksetzen
            </button>
          ) : null}
          <span className="table-count" aria-live="polite" aria-atomic="true">
            {rows.length === data.findings.length
              ? `${fmtNumber(rows.length)} ${plural(rows.length, 'Befund', 'Befunde')}`
              : `${fmtNumber(rows.length)} von ${fmtNumber(data.findings.length)} Befunden`}
          </span>
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title={data.findings.length === 0
            ? 'Dependency-Track meldet keine Befunde für diese Instanz'
            : 'Kein Befund passt zu den Filtern'}
        />
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(row) => `${row.cve_id}-${row.component}-${row.component_version}`}
          renderDetail={renderDetail}
          caption="Schwachstellenbefunde der ausgewählten Instanz"
          pageSize={15}
          resetPageOnRowsChange={false}
          paginationResetKey={`${severity}|${onlyKev}|${search}|${includeSuppressed}`}
        />
      )}
    </div>
  );
}

export default FindingsPanel;
